/* eslint-disable react/prop-types */
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { DataGrid } from '@mui/x-data-grid';

const modalGridSx = {
	border: '1px solid #e6edf7',
	borderRadius: '6px',
	color: '#172d58',
	fontFamily: 'Poppins',
	fontSize: {
		xl: '12px',
		lg: '11px',
		md: '11px',
		sm: '10px',
		xs: '10px',
	},
	'& .MuiDataGrid-columnHeaders': {
		backgroundColor: '#f7f9fc',
		color: '#172d58',
		borderBottom: '1px solid #e5ebf4',
		borderTopLeftRadius: '6px',
		borderTopRightRadius: '6px',
		minHeight: '34px !important',
		maxHeight: '34px !important',
	},
	'& .MuiDataGrid-columnHeader': {
		outline: 'none !important',
	},
	'& .MuiDataGrid-columnHeaderTitle': {
		fontWeight: 700,
		fontSize: '11px',
	},
	'& .MuiDataGrid-cell': {
		borderBottom: '1px solid #edf1f6',
		outline: 'none !important',
		fontWeight: 500,
	},
	'& .MuiDataGrid-row:hover': {
		backgroundColor: '#fafcff',
	},
	'& .MuiDataGrid-footerContainer': {
		borderTop: '1px solid #e5ebf4',
		minHeight: '54px',
		backgroundColor: '#fff',
		justifyContent: 'flex-end',
		px: 1.5,
	},
	'& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
	{
		fontFamily: 'Poppins',
		fontSize: '12px',
		color: '#172d58',
		fontWeight: 600,
	},
	'& .MuiTablePagination-toolbar': {
		minHeight: '44px',
		gap: '8px',
		pr: 0,
	},
	'& .MuiTablePagination-spacer': {
		display: 'none',
	},
	'& .MuiTablePagination-select': {
		border: '1px solid #dce5f2',
		borderRadius: '8px',
		padding: '6px 28px 6px 12px !important',
		color: '#172d58',
		fontWeight: 600,
		backgroundColor: '#fff',
	},
	'& .MuiTablePagination-selectIcon': {
		color: '#60739d',
	},
	'& .MuiTablePagination-actions': {
		display: 'flex',
		gap: '8px',
		ml: 1,
	},
	'& .MuiTablePagination-actions .MuiIconButton-root': {
		width: '34px',
		height: '34px',
		border: '1px solid #e4ebf6',
		color: '#1f66ff',
		backgroundColor: '#fff',
	},
	'& .MuiTablePagination-actions .MuiIconButton-root:hover': {
		backgroundColor: '#f4f8ff',
	},
	'& .MuiTablePagination-actions .MuiIconButton-root.Mui-disabled': {
		color: '#b9c7dc',
		borderColor: '#e4ebf6',
		backgroundColor: '#fff',
	},
	'& .MuiDataGrid-virtualScroller': {
		scrollbarWidth: 'thin',
	},
	'& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
		width: '6px',
		height: '6px',
	},
	'& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb': {
		backgroundColor: '#c7d4e8',
		borderRadius: '999px',
	},
};

const getModalGridContainerSx = (rowCount = 0, rowHeight = 40) => {
	const visibleRows = Math.max(1, Math.min(rowCount || 1, 10));
	const headerHeight = 34;
	const footerHeight = 54;
	const borderBuffer = 2;

	const calculatedHeight =
		headerHeight +
		visibleRows * rowHeight +
		footerHeight +
		borderBuffer +
		24;

	return {
		width: '100%',
		height:
			headerHeight + visibleRows * rowHeight + footerHeight + borderBuffer + 24,
		minHeight: '520px',
		height: calculatedHeight,
		maxHeight: {
			xs: 'calc(86vh - 41px)',
			sm: 'calc(82vh - 41px)',
			md: 'calc(80vh - 41px)',
			lg: 'calc(82vh - 41px)',
			xl: 'calc(80vh - 41px)',
		},
		overflow: 'hidden',
		p: '12px',
		boxSizing: 'border-box',
	};
};

const getModalColumns = (columns) =>
	columns.map((column) => ({
		...column,
		minWidth: column.minWidth || column.width || 120,
		flex:
			column.flex ||
			(column.field === 'codes'
				? 2.1
				: column.field === 'locationName'
					? 1.45
					: column.field === 'patientName'
						? 1.25
						: column.field === 'providerName'
							? 1.2
							: 1),
	}));

// Batches Modal Child Component of dashboard left side
export function BatchesModalBox(props) {
	const [batchesVisitData, setBatchesVisitData] = useState([]);
	const columns = [
		{ field: 'patientName', headerName: 'Patient Name', width: 130 },
		{ field: 'providerName', headerName: 'Provider Name', width: 130 },
		{ field: 'visitDate', headerName: 'Visit Date', width: 120 },
		{ field: 'AdmissionDate', headerName: 'Admission Date', width: 130 },
		{
			field: 'codes',
			headerName: 'Codes',
			width: 250,
			renderCell: (items) => {
				return (
					<Box
						style={
							{
								// width: '100%',
								// height: '60px',
								// overflow: 'auto',
							}
						}
						sx={{
							'&::-webkit-scrollbar': {
								width: '1px',
								height: '3px',
							},
							'&::-webkit-scrollbar-track': {
								background: 'grey',
							},
							'&::-webkit-scrollbar-thumb': {
								background: '#0E4374',
								borderRadius: '5px',
							},
						}}
						display={'flex'}
						flexDirection={'column'}
					>
						<span
							style={{
								color: '#4D7BDE',
								fontWeight: 600,
								fontSize: '13px',
								width: '100%',
							}}
						>
							{' '}
							{items?.row?.visit_cpts?.length > 0
								? items?.row?.visit_cpts?.map(
									(data) => data?.CPTCode !== null && data?.CPTCode + ','
								)
								: ' '}
						</span>
						<Box fontSize={'13px'}>
							<Tooltip
								title={
									<>
										{items?.row?.Dx1Description &&
											`${items?.row?.Dxcode1} ${items?.row?.Dx1Description}`}
										{items?.row?.Dx1Description && <br />}
										{items?.row?.Dx2Description &&
											`${items?.row?.Dxcode2} ${items?.row?.Dx2Description}`}
										{items?.row?.Dx2Description && <br />}
										{items?.row?.Dx3Description &&
											`${items?.row?.Dxcode3} ${items?.row?.Dx3Description}`}
										{items?.row?.Dx3Description && <br />}
										{items?.row?.Dx4Description &&
											`${items?.row?.Dxcode4} ${items?.row?.Dx4Description}`}
										{items?.row?.Dx4Description && <br />}
										{items?.row?.Dx5Description &&
											`${items?.row?.Dxcode5} ${items?.row?.Dx5Description}`}
										{items?.row?.Dx5Description && <br />}
										{items?.row?.Dx6Description &&
											`${items?.row?.Dxcode6} ${items?.row?.Dx6Description}`}
										{items?.row?.Dx6Description && <br />}
										{items?.row?.Dx7Description &&
											`${items?.row?.Dxcode7} ${items?.row?.Dx7Description}`}
										{items?.row?.Dx7Description && <br />}
										{items?.row?.Dx8Description &&
											`${items?.row?.Dxcode8} ${items?.row?.Dx8Description}`}
										{items?.row?.Dx8Description && <br />}
										{items?.row?.Dx9Description &&
											`${items?.row?.Dxcode9} ${items?.row?.Dx9Description}`}
										{items?.row?.Dx9Description && <br />}
										{items?.row?.Dx10Description &&
											`${items?.row?.Dxcode10} ${items?.row?.Dx10Description}`}
										{items?.row?.Dx10Description && <br />}
										{items?.row?.Dx11Description &&
											`${items?.row?.Dxcode11} ${items?.row?.Dx11Description}`}
										{items?.row?.Dx11Description && <br />}
										{items?.row?.Dx12Description &&
											`${items?.row?.Dxcode12} ${items?.row?.Dx12Description}`}
										{items?.row?.Dx12Description && <br />}
									</>
								}
							>
								<Typography
									variant="body2"
									sx={{
										textOverflow: 'ellipsis',
										overflow: 'hidden',
										whiteSpace: 'nowrap',
										width: 200,
										fontSize: {
											xl: '13px',
											lg: '10px',
											md: '10px',
											sm: '10px',
											xs: '10px',
										},
									}}
								>
									{items?.row?.Dxcode1} {items?.row?.Dx1Description}
									{/* <br />
									{items?.row?.Dxcode2} {items?.row?.Dx2Description}{' '} */}
								</Typography>
							</Tooltip>
						</Box>
					</Box>
				);
			},
		},

		{ field: 'locationName', headerName: 'Location', width: 130 },
		{
			field: 'VisitStatus',
			headerName: 'Status',
			width: 60,
			// renderCell: (params) => {
			// 	return (
			// 		<span
			// 			style={{
			// 				fontFamily: 'Poppins',
			// 				fontSize: '13px',
			// 				color: '#FEAB13',
			// 			}}
			// 		>
			// 			{params?.VisitStatus}
			// 		</span>
			// 	);
			// },
		},
	];

	useEffect(() => {
		setBatchesVisitData(
			props.batchesVisitDataProp.map((item) => ({
				...item,
				patientName: `${item?.patient?.Fname} ${item?.patient?.Lname}`,
				providerName: `${item?.practicestaff?.Fname}, ${item?.practicestaff?.Lname}`,
				visitDate: `${dayjs(item?.VisitDate).format(`DD/MM/YYYY  `)}`,
				AdmissionDate: `${dayjs(item?.admission?.Admission_date).format('MM/DD/YYYY')}`,
				locationName: item?.location?.Name,
				VisitStatus:
					item?.VisitStatus === 'B'
						? 'Billed'
						: item?.VisitStatus === 'D'
							? 'Draft'
							: item?.VisitStatus === 'A'
								? 'Approved'
								: item?.VisitStatus === 'O'
									? 'Others'
									: '',
			}))
		);
	}, []);
	return (
		<div>
			<Box
				sx={getModalGridContainerSx(batchesVisitData.length)}
			>
				{' '}
				<DataGrid
					rows={batchesVisitData}
					columns={getModalColumns(columns)}
					rowHeight={40}
					columnHeaderHeight={35}
					fontFamily={'Poppins'}
					// checkboxSelection
					disableRowSelectionOnClick
					sx={modalGridSx}
					pageSizeOptions={[10, 25, 50, 100]}
				/>
			</Box>
		</div>
	);
}

export function DraftVisits(props) {
	const [pageSizeOptions, setPageSizeOptions] = useState(10); // Set the initial page size
	const [pagination, setPagination] = useState(null);
	const [draftVisitData, setDraftVisitData] = useState([]);

	useEffect(() => {
		setDraftVisitData(
			props.draftVisitsDataProp.map((item) => ({
				...item,
				patientName: `${item?.patient?.Fname} ${item?.patient?.Lname}`,
				providerName: `${item?.provider}`,
				visitDate: `${dayjs(item?.VisitDate).format(`MM/DD/YYYY  `)}`,
				AdmissionDate: `${dayjs(item?.admission?.Admission_date).format('MM/DD/YYYY')}`,
				locationName: item?.location,
				visitStatus: item?.VisitStatus === 'D' ? 'Draft' : '',
			}))
		);
	}, [props.draftVisitsDataProp]);

	const columns = [
		{ field: 'patientName', headerName: 'Patient Name', width: 130 },
		{
			field: 'providerName',
			headerName: 'Provider Name',
			width: 130,
			renderCell: (params) => {
				return (
					<Box>
						<span>
							{params?.row?.provider == ', '
								? params?.row?.provider == ''
								: params?.row?.provider}
						</span>
					</Box>
				);
			},
		},
		{ field: 'visitDate', headerName: 'Visit Date', width: 120 },
		{
			field: 'AdmissionDate',
			headerName: 'Admission Date',
			width: 130,
		},
		{
			field: 'codes',
			headerName: 'Codes',
			width: 250,
			renderCell: (items) => {
				return (
					<Box
						fontSize={'13px'}
						sx={{
							textOverflow: 'ellipsis',
							overflow: 'hidden',
							whiteSpace: 'nowrap',
						}}
					>
						<Tooltip
							title={
								<>
									{items?.row?.Dx1Description &&
										`${items?.row?.Dxcode1} ${items?.row?.Dx1Description}`}
									{items?.row?.Dx1Description && <br />}
									{items?.row?.Dx2Description &&
										`${items?.row?.Dxcode2} ${items?.row?.Dx2Description}`}
									{items?.row?.Dx2Description && <br />}
									{items?.row?.Dx3Description &&
										`${items?.row?.Dxcode3} ${items?.row?.Dx3Description}`}
									{items?.row?.Dx2Description && <br />}
									{items?.row?.Dx4Description &&
										`${items?.row?.Dxcode4} ${items?.row?.Dx4Description}`}
									{items?.row?.Dx4Description && <br />}
									{items?.row?.Dx5Description &&
										`${items?.row?.Dxcode5} ${items?.row?.Dx5Description}`}
									{items?.row?.Dx5Description && <br />}
									{items?.row?.Dx6Description &&
										`${items?.row?.Dxcode6} ${items?.row?.Dx6Description}`}
									{items?.row?.Dx6Description && <br />}
									{items?.row?.Dx7Description &&
										`${items?.row?.Dxcode7} ${items?.row?.Dx7Description}`}
									{items?.row?.Dx7Description && <br />}
									{items?.row?.Dx8Description &&
										`${items?.row?.Dxcode8} ${items?.row?.Dx8Description}`}
									{items?.row?.Dx8Description && <br />}
									{items?.row?.Dx9Description &&
										`${items?.row?.Dxcode9} ${items?.row?.Dx9Description}`}
									{items?.row?.Dx9Description && <br />}
									{items?.row?.Dx10Description &&
										`${items?.row?.Dxcode10} ${items?.row?.Dx10Description}`}
									{items?.row?.Dx10Description && <br />}
									{items?.row?.Dx11Description &&
										`${items?.row?.Dxcode11} ${items?.row?.Dx11Description}`}
									{items?.row?.Dx11Description && <br />}
									{items?.row?.Dx12Description &&
										`${items?.row?.Dxcode12} ${items?.row?.Dx12Description}`}
								</>
							}
						>
							<Typography
								variant="body2"
								sx={{
									color: '#4D7BDE',
									// fontWeight: 600,
									fontSize: {
										xl: '13px',
										lg: '10px',
										md: '10px',
										xs: '10px',
										sm: '10px',
									},
									width: '100%',
									textOverflow: 'ellipsis',
									overflow: 'hidden',
									whiteSpace: 'nowrap',
								}}
							>
								{' '}
								{items?.row?.visit_cpts?.length > 0
									? items?.row?.visit_cpts?.map(
										(data) =>
											data?.CPTCode !== null &&
											data?.CPTCode + (data?.CPTCode?.length > 0 ? ',' : '')
									)
									: ' '}
							</Typography>
							<Typography
								variant="body2"
								sx={{
									fontSize: {
										xl: '13px',
										lg: '10px',
										md: '10px',
										xs: '10px',
										sm: '10px',
									},
									textOverflow: 'ellipsis',
									overflow: 'hidden',
									whiteSpace: 'nowrap',
									width: 200,
								}}
							>
								{items?.row?.Dxcode1} {items?.row?.Dx1Description}
								{/* <br /> */}
								{/* {items?.row?.Dxcode2} {items?.row?.Dx2Description}{' '} */}
							</Typography>
						</Tooltip>
					</Box>
				);
			},
		},

		{ field: 'locationName', headerName: 'Location', width: 130 },
	];
	return (
		<div>
			<Box
				sx={getModalGridContainerSx(draftVisitData.length)}
			>
				{' '}
				<DataGrid
					rows={draftVisitData}
					columns={getModalColumns(columns)}
					rowHeight={40}
					columnHeaderHeight={35}
					fontFamily={'Poppins'}
					// checkboxSelection
					disableRowSelectionOnClick
					initialState={{
						pagination: { paginationModel: { pageSize: 10 } },
					}}
					sx={modalGridSx}
					pageSizeOptions={[10, 25, 50, 100]}
				/>
			</Box>
		</div>
	);
}

export function ApprovedVisits(props) {
	const [approvedVisitData, setApprovedVisitData] = useState([]);

	useEffect(() => {
		setApprovedVisitData(
			props.approvedVisitsDataProp.map((item) => ({
				...item,
				patientName: `${item?.patient?.Fname} ${item?.patient?.Lname}`,
				providerName: `${item?.provider}`,
				visitDate: `${dayjs(item?.VisitDate).format(`MM/DD/YYYY  `)}`,
				AdmissionDate: `${dayjs(item?.admission?.Admission_date).format('MM/DD/YYYY')}`,
				locationName: item?.location,
				visitStatus: item?.VisitStatus === 'A' ? 'Approved' : '',
			}))
		);
	}, [props.approvedVisitsDataProp]);
	const columns = [
		{ field: 'patientName', headerName: 'Patient Name', width: 130 },
		{
			field: 'providerName',
			headerName: 'Provider Name',
			width: 130,
			renderCell: (params) => {
				return (
					<Box>
						<span>
							{params?.row?.provider == ', '
								? params?.row?.provider == ''
								: params?.row?.provider}
						</span>
					</Box>
				);
			},
		},
		{ field: 'visitDate', headerName: 'Visit Date', width: 120 },
		{ field: 'AdmissionDate', headerName: 'Admission Date', width: 130 },
		{
			field: 'codes',
			headerName: 'Codes',
			width: 250,
			renderCell: (items) => {
				return (
					<Box
						sx={{
							'&::-webkit-scrollbar': {
								width: '1px',
								height: '3px',
							},
							'&::-webkit-scrollbar-track': {
								background: 'grey',
							},
							'&::-webkit-scrollbar-thumb': {
								background: '#0E4374',
								borderRadius: '5px',
							},
						}}
						display={'flex'}
						flexDirection={'column'}
					>
						<span
							style={{
								color: '#4D7BDE',
								fontWeight: 600,
								fontSize: {
									xl: '13px',
									lg: '10px',
									md: '10px',
									xs: '10px',
									sm: '10px',
								},
								width: '100%',
							}}
						>
							{' '}
							{items?.row?.visit_cpts?.length > 0
								? items?.row?.visit_cpts?.map(
									(data) =>
										data?.CPTCode !== null &&
										data?.CPTCode + (data?.CPTCode?.length > 0 ? ',' : '')
								)
								: ' '}
						</span>
						<Box fontSize={'10px'}>
							<Tooltip
								title={
									<>
										{items?.row?.Dx1Description &&
											`${items?.row?.Dxcode1} ${items?.row?.Dx1Description}`}
										{items?.row?.Dx1Description && <br />}
										{items?.row?.Dx2Description &&
											`${items?.row?.Dxcode2} ${items?.row?.Dx2Description}`}
										{items?.row?.Dx2Description && <br />}
										{items?.row?.Dx3Description &&
											`${items?.row?.Dxcode3} ${items?.row?.Dx3Description}`}
										{items?.row?.Dx3Description && <br />}
										{items?.row?.Dx4Description &&
											`${items?.row?.Dxcode4} ${items?.row?.Dx4Description}`}
										{items?.row?.Dx4Description && <br />}
										{items?.row?.Dx5Description &&
											`${items?.row?.Dxcode5} ${items?.row?.Dx5Description}`}
										{items?.row?.Dx5Description && <br />}
										{items?.row?.Dx6Description &&
											`${items?.row?.Dxcode6} ${items?.row?.Dx6Description}`}
										{items?.row?.Dx6Description && <br />}
										{items?.row?.Dx7Description &&
											`${items?.row?.Dxcode7} ${items?.row?.Dx7Description}`}
										{items?.row?.Dx7Description && <br />}
										{items?.row?.Dx8Description &&
											`${items?.row?.Dxcode8} ${items?.row?.Dx8Description}`}
										{items?.row?.Dx8Description && <br />}
										{items?.row?.Dx9Description &&
											`${items?.row?.Dxcode9} ${items?.row?.Dx9Description}`}
										{items?.row?.Dx9Description && <br />}
										{items?.row?.Dx10Description &&
											`${items?.row?.Dxcode10} ${items?.row?.Dx10Description}`}
										{items?.row?.Dx10Description && <br />}
										{items?.row?.Dx11Description &&
											`${items?.row?.Dxcode11} ${items?.row?.Dx11Description}`}
										{items?.row?.Dx11Description && <br />}
										{items?.row?.Dx12Description &&
											`${items?.row?.Dxcode12} ${items?.row?.Dx12Description}`}
										{items?.row?.Dx12Description && <br />}
									</>
								}
							>
								<Typography
									variant="body2"
									sx={{
										textOverflow: 'ellipsis',
										overflow: 'hidden',
										whiteSpace: 'nowrap',
										width: 200,
										fontSize: {
											xl: '13px',
											lg: '10px',
											md: '10px',
											xs: '10px',
											sm: '10px',
										},
									}}
								>
									{items?.row?.Dxcode1} {items?.row?.Dx1Description}
									{/* <br />
									{items?.row?.Dxcode2} {items?.row?.Dx2Description}{' '} */}
								</Typography>
							</Tooltip>
						</Box>
					</Box>
				);
			},
		},

		{ field: 'locationName', headerName: 'Location', width: 130 },
	];

	return (
		<div>
			<Box
				sx={getModalGridContainerSx(approvedVisitData.length)}
			>
				{' '}
				<DataGrid
					rows={approvedVisitData}
					columns={getModalColumns(columns)}
					rowHeight={40}
					columnHeaderHeight={35}
					fontFamily={'Poppins'}
					loading={props?.loading}
					disableRowSelectionOnClick
					initialState={{
						pagination: { paginationModel: { pageSize: 10 } },
					}}
					sx={modalGridSx}
					pageSizeOptions={[10, 25, 50, 100]}
				/>
			</Box>
		</div>
	);
}

export function RejectedVisits(props) {
	const [otherVisitData, setOtherVisitData] = useState([]);

	// useEffect(() => {
	// 	setOtherVisitData(props.otherVisitsDataProp);
	// }, []);

	useEffect(() => {
		setOtherVisitData(
			props.otherVisitsDataProp.map((item) => ({
				...item,
				patientName: `${item?.patient?.Fname} ${item?.patient?.Lname}`,
				providerName: `${item?.practicestaff?.Fname}, ${item?.practicestaff?.Lname}`,
				visitDate: `${dayjs(item?.VisitDate).format(`DD/MM/YYYY  `)}`,
				AdmissionDate: `${dayjs(item?.admission?.Admission_date).format('MM/DD/YYYY')}`,
				locationName: item?.location?.Name,
				visitStatus: item?.VisitStatus === 'O' ? 'Other' : '',
			}))
		);
	}, [props.otherVisitsDataProp]);
	const columns = [
		{ field: 'patientName', headerName: 'Patient Name', width: 130 },
		{ field: 'providerName', headerName: 'Provider Name', width: 130 },
		{ field: 'visitDate', headerName: 'Visit Date', width: 120 },
		{ field: 'AdmissionDate', headerName: 'Admission Date', width: 130 },
		{
			field: 'codes',
			headerName: 'Codes',
			width: 250,
			renderCell: (items) => {
				return (
					<Box
						style={
							{
								// width: '100%',
								// height: '60px',
								// overflow: 'auto',
							}
						}
						sx={{
							'&::-webkit-scrollbar': {
								width: '1px',
								height: '3px',
							},
							'&::-webkit-scrollbar-track': {
								background: 'grey',
							},
							'&::-webkit-scrollbar-thumb': {
								background: '#0E4374',
								borderRadius: '5px',
							},
						}}
						display={'flex'}
						flexDirection={'column'}
					>
						<Typography
							sx={{
								color: '#4D7BDE',
								fontWeight: 600,
								fontSize: {
									xl: '13px',
									lg: '10px',
									md: '10px',
									sm: '10px',
									xs: '10px',
								},
								width: '100%',
							}}
						>
							{' '}
							{items?.row?.visit_cpts?.length > 0
								? items?.row?.visit_cpts?.map(
									(data) => data?.CPTCode !== null && data?.CPTCode + ','
								)
								: ' '}
						</Typography>
						<Box fontSize={'13px'}>
							<Tooltip
								title={
									<>
										{items?.row?.Dx1Description &&
											`${items?.row?.Dxcode1} ${items?.row?.Dx1Description}`}
										{items?.row?.Dx1Description && <br />}
										{items?.row?.Dx2Description &&
											`${items?.row?.Dxcode2} ${items?.row?.Dx2Description}`}
										{items?.row?.Dx2Description && <br />}
										{items?.row?.Dx3Description &&
											`${items?.row?.Dxcode3} ${items?.row?.Dx3Description}`}
										{items?.row?.Dx3Description && <br />}
										{items?.row?.Dx4Description &&
											`${items?.row?.Dxcode4} ${items?.row?.Dx4Description}`}
										{items?.row?.Dx4Description && <br />}
										{items?.row?.Dx5Description &&
											`${items?.row?.Dxcode5} ${items?.row?.Dx5Description}`}
										{items?.row?.Dx5Description && <br />}
										{items?.row?.Dx6Description &&
											`${items?.row?.Dxcode6} ${items?.row?.Dx6Description}`}
										{items?.row?.Dx6Description && <br />}
										{items?.row?.Dx7Description &&
											`${items?.row?.Dxcode7} ${items?.row?.Dx7Description}`}
										{items?.row?.Dx7Description && <br />}
										{items?.row?.Dx8Description &&
											`${items?.row?.Dxcode8} ${items?.row?.Dx8Description}`}
										{items?.row?.Dx8Description && <br />}
										{items?.row?.Dx9Description &&
											`${items?.row?.Dxcode9} ${items?.row?.Dx9Description}`}
										{items?.row?.Dx9Description && <br />}
										{items?.row?.Dx10Description &&
											`${items?.row?.Dxcode10} ${items?.row?.Dx10Description}`}
										{items?.row?.Dx10Description && <br />}
										{items?.row?.Dx11Description &&
											`${items?.row?.Dxcode11} ${items?.row?.Dx11Description}`}
										{items?.row?.Dx11Description && <br />}
										{items?.row?.Dx12Description &&
											`${items?.row?.Dxcode12} ${items?.row?.Dx12Description}`}
										{items?.row?.Dx12Description && <br />}
									</>
								}
							>
								<Typography
									variant="body2"
									sx={{
										textOverflow: 'ellipsis',
										overflow: 'hidden',
										whiteSpace: 'nowrap',
										width: 200,
										fontSize: {
											xl: '13px',
											lg: '10px',
											md: '10px',
											xs: '10px',
											sm: '10px',
										},
									}}
								>
									{items?.row?.Dxcode1} {items?.row?.Dx1Description}
								</Typography>
							</Tooltip>
						</Box>
					</Box>
				);
			},
		},

		{ field: 'locationName', headerName: 'Location', width: 130 },
	];
	return (
		<div>
			{otherVisitData?.length === 0 && (
				<Box
					width={'100%'}
					display={'flex'}
					justifyContent={'center'}
					alignItems={'center'}
					mt={1}
				>
					{/* 0 Record */}
					<CircularProgress />
				</Box>
			)}

			<Box
				sx={getModalGridContainerSx(otherVisitData.length)}
			>
				{' '}
				<DataGrid
					rows={otherVisitData}
					columns={getModalColumns(columns)}
					rowHeight={40}
					columnHeaderHeight={35}
					sx={modalGridSx}
					fontFamily={'Poppins'}
					// checkboxSelection
					disableRowSelectionOnClick
					initialState={{
						pagination: { paginationModel: { pageSize: 10 } },
					}}
					pageSizeOptions={[10, 25, 50, 100]}
				/>
			</Box>
		</div>
	);
}
export function BilledVisits(props) {
	const [billedVisitData, setBilledVisitData] = useState([]);

	// useEffect(() => {
	// 	setBilledVisitData(props.billedvisitsDataProp);
	// }, []);

	useEffect(() => {
		setBilledVisitData(
			props?.billedvisitsDataProp?.map((item) => ({
				...item,
				patientName: `${item?.patient?.Fname} ${item?.patient?.Lname}`,
				providerName: `${item?.provider}`,
				visitDate: `${dayjs(item?.VisitDate).format(`MM/DD/YYYY  `)}`,
				AdmissionDate: `${dayjs(item?.admission?.Admission_date).format('MM/DD/YYYY')}`,
				locationName: item?.location,
				visitStatus: item?.VisitStatus === 'B' ? 'Billed' : '',
			}))
		);
	}, [props.billedvisitsDataProp]);
	const columns = [
		{ field: 'patientName', headerName: 'Patient Name', width: 130 },
		{
			field: 'providerName',
			headerName: 'Provider Name',
			width: 130,
			renderCell: (params) => {
				return (
					<Box>
						<span>
							{params?.row?.provider == ', '
								? params?.row?.provider == ''
								: params?.row?.provider}
						</span>
					</Box>
				);
			},
		},
		{ field: 'visitDate', headerName: 'Visit Date', width: 120 },
		{ field: 'AdmissionDate', headerName: 'Admission Date', width: 130 },
		{
			field: 'codes',
			headerName: 'Codes',
			width: 250,
			renderCell: (items) => {
				return (
					<Box
						sx={{
							'&::-webkit-scrollbar': {
								width: '1px',
								height: '3px',
							},
							'&::-webkit-scrollbar-track': {
								background: 'grey',
							},
							'&::-webkit-scrollbar-thumb': {
								background: '#0E4374',
								borderRadius: '5px',
							},
						}}
						display={'flex'}
						flexDirection={'column'}
					>
						<Typography
							sx={{
								color: '#4D7BDE',
								// fontWeight: 600,
								fontSize: {
									xl: '13px',
									lg: '10px',
									md: '10px',
									sm: '10px',
									xs: '10px',
								},
								width: '100%',
							}}
						>
							{' '}
							{items?.row?.visit_cpts?.length > 0
								? items?.row?.visit_cpts?.map(
									(data) =>
										data?.CPTCode !== null &&
										data?.CPTCode + (data?.CPTCode?.length > 0 ? ',' : '')
								)
								: ' '}
						</Typography>
						<Box fontSize={'13px'}>
							<Tooltip
								title={
									<>
										{items?.row?.Dx1Description &&
											`${items?.row?.Dxcode1} ${items?.row?.Dx1Description}`}
										{items?.row?.Dx1Description && <br />}
										{items?.row?.Dx2Description &&
											`${items?.row?.Dxcode2} ${items?.row?.Dx2Description}`}
										{items?.row?.Dx2Description && <br />}
										{items?.row?.Dx3Description &&
											`${items?.row?.Dxcode3} ${items?.row?.Dx3Description}`}
										{items?.row?.Dx3Description && <br />}
										{items?.row?.Dx4Description &&
											`${items?.row?.Dxcode4} ${items?.row?.Dx4Description}`}
										{items?.row?.Dx4Description && <br />}
										{items?.row?.Dx5Description &&
											`${items?.row?.Dxcode5} ${items?.row?.Dx5Description}`}
										{items?.row?.Dx5Description && <br />}
										{items?.row?.Dx6Description &&
											`${items?.row?.Dxcode6} ${items?.row?.Dx6Description}`}
										{items?.row?.Dx6Description && <br />}
										{items?.row?.Dx7Description &&
											`${items?.row?.Dxcode7} ${items?.row?.Dx7Description}`}
										{items?.row?.Dx7Description && <br />}
										{items?.row?.Dx8Description &&
											`${items?.row?.Dxcode8} ${items?.row?.Dx8Description}`}
										{items?.row?.Dx8Description && <br />}
										{items?.row?.Dx9Description &&
											`${items?.row?.Dxcode9} ${items?.row?.Dx9Description}`}
										{items?.row?.Dx9Description && <br />}
										{items?.row?.Dx10Description &&
											`${items?.row?.Dxcode10} ${items?.row?.Dx10Description}`}
										{items?.row?.Dx10Description && <br />}
										{items?.row?.Dx11Description &&
											`${items?.row?.Dxcode11} ${items?.row?.Dx11Description}`}
										{items?.row?.Dx11Description && <br />}
										{items?.row?.Dx12Description &&
											`${items?.row?.Dxcode12} ${items?.row?.Dx12Description}`}
										{items?.row?.Dx12Description && <br />}
									</>
								}
							>
								<Typography
									variant="body2"
									sx={{
										textOverflow: 'ellipsis',
										overflow: 'hidden',
										whiteSpace: 'nowrap',
										width: 200,
										fontSize: {
											xl: '13px',
											lg: '10px',
											md: '10px',
											xs: '10px',
											sm: '10px',
										},
									}}
								>
									{items?.row?.Dxcode1} {items?.row?.Dx1Description}
									{/* <br />
									{items?.row?.Dxcode2} {items?.row?.Dx2Description}{' '} */}
								</Typography>
							</Tooltip>
						</Box>
					</Box>
				);
			},
		},

		{ field: 'locationName', headerName: 'Location', width: 130 },
	];

	return (
		<div>
			<Box
				sx={getModalGridContainerSx(billedVisitData.length)}
			>
				{' '}
				<DataGrid
					rows={billedVisitData}
					columns={getModalColumns(columns)}
					rowHeight={40}
					columnHeaderHeight={35}
					sx={modalGridSx}
					fontFamily={'Poppins'}
					// checkboxSelection
					disableRowSelectionOnClick
					loading={props?.loading}
					initialState={{
						pagination: { paginationModel: { pageSize: 10 } },
					}}
					pageSizeOptions={[10, 25, 50, 100]}
				/>
			</Box>
		</div>
	);
}

export function AllAppointments(props) {
	const [appointmentList, setAppointmentList] = useState([]);

	useEffect(() => {
		setAppointmentList(
			props?.appointmentsDataProp
				?.filter((item) => item?.Status === 'P')
				.map((item) => ({
					...item,
					patientName: `${item?.patient?.Fname} ${item?.patient?.Lname}`,
					Reason: item?.Reason,
					locationName: item?.location?.Name,
					AppointmentDate: dayjs(item?.AppointmentDate).format(
						'DD/MM/YYYY HH:mm:ss A'
					),
					// status: item?.Status=="P"?"Pending":item?.Status=="A"?"Approved":item?.Status=="R"?"Other",
				}))
		);
	}, [props?.appointmentsDataProp]);

	const columns = [
		{ field: 'patientName', headerName: 'Patient Name', width: 150 },
		{
			field: 'Reason',
			headerName: 'Reason',
			width: 150,
		},
		{ field: 'locationName', headerName: 'Location', width: 150 },
		{ field: 'AppointmentDate', headerName: 'Date', width: 150 },
		{
			field: 'status',
			headerName: 'Status',
			width: 130,
			renderCell: (item) => {
				return (
					<Typography
						// fontSize={'13px'}
						fontWeight={500}
						sx={{
							color:
								item?.row?.Status === 'C'
									? 'green'
									: item?.row?.Status === 'D'
										? 'red'
										: item?.row?.Status === 'O'
											? 'grey'
											: '#CBC32A',
							fontSize: {
								xl: '13px',
								lg: '10px',
								md: '10px',
								xs: '10px',
								sm: '10px',
							},
						}}
					>
						{item?.row?.Status === 'C'
							? 'Confirmed'
							: item?.row?.Status === 'D'
								? 'Dismissed'
								: item?.row?.Status === 'O'
									? 'Other'
									: 'Pending'}
					</Typography>
				);
			},
		},
	];
	return (
		<div>
			<Box
				sx={getModalGridContainerSx(appointmentList.length, 35)}
			>
				{' '}
				<DataGrid
					rows={appointmentList}
					columns={getModalColumns(columns)}
					rowHeight={35}
					columnHeaderHeight={35}
					sx={modalGridSx}
					fontFamily={'Poppins'}
					loading={props?.loading}
					disableRowSelectionOnClick
					initialState={{
						pagination: { paginationModel: { pageSize: 10 } },
					}}
					pageSizeOptions={[10, 25, 50, 100]}
				/>
			</Box>
		</div>
	);
}

export function ActivePatients(props) {
	const [activePatients, setActivePatients] = useState(props?.activePatientDataProp || []);

	useEffect(() => {
		setActivePatients(props?.activePatientDataProp || []);
	}, [props?.activePatientDataProp]);

	const columns = [
		{
			field: 'Name',
			headerName: 'Patient',
			flex: 1,
			minWidth: 150,
			renderCell: (params) => {
				return (
					<Box>
						<span>
							{params?.row?.Name == ', '
								? params?.row?.Name == ''
								: params?.row?.Name}
						</span>
					</Box>
				);
			},
		},
		{
			field: 'provider',
			headerName: 'Provider',
			flex: 1,
			minWidth: 120,
			renderCell: (params) => {
				return (
					<Box>
						<span>
							{params?.row?.provider == ', '
								? params?.row?.provider == ''
								: params?.row?.provider}
						</span>
					</Box>
				);
			},
		},
		{
			field: 'MRN',
			headerName: 'MRN',
			flex: 0.5,
			minWidth: 100,
		},
		{
			field: 'Admission_date',
			headerName: 'Admission Date',
			flex: 0.5,
			minWidth: 150,
			renderCell: (params) => {
				return (
					params?.row?.Admission_date !== null && (
						<span>
							{dayjs(params?.row?.Admission_date).format('MM/DD/YYYY')}
						</span>
					)
				);
			},
		},
		{
			field: 'location',
			headerName: 'Location',
			flex: 1,
			minWidth: 150,
		},
		{
			field: 'DOB',
			headerName: 'DOB',
			flex: 0.5,
			minWidth: 100,
			renderCell: (params) => {
				return (
					params?.row?.DOB !== null && (
						<span>{dayjs(params?.row?.DOB).format('MM/DD/YYYY')}</span>
					)
				);
			},
		},
		{
			field: 'Inactive',
			headerName: 'Status',
			flex: 0.5,
			minWidth: 110,
			renderCell: (params) => {
				return (
					<Typography
						fontWeight={500}
						sx={{
							color: 'green',
							fontSize: {
								xl: '13px',
								xs: '10px',
							},
						}}
					>
						{params?.row?.Inactive === false || params?.row?.Inactive === null
							? 'Active'
							: ''}
					</Typography>
				);
			},
		},
		{
			field: 'discharge_date',
			headerName: 'Discharge Date',
			flex: 0.5,
			minWidth: 150,
			renderCell: (params) => {
				return (
					params?.row?.discharge_date !== null && (
						<span>
							{dayjs(params?.row?.discharge_date).format('MM/DD/YYYY')}
						</span>
					)
				);
			},
		},
	];
	return (
		<div>
			<Box
				sx={getModalGridContainerSx(activePatients.length, 35)}
			>
				{' '}
				<DataGrid
					rows={activePatients}
					columns={getModalColumns(columns)}
					rowHeight={35}
					columnHeaderHeight={35}
					initialState={{
						pagination: { paginationModel: { pageSize: 10 } },
					}}
					pageSizeOptions={[10, 25, 50, 100]}
					loading={props?.loading}
					sx={modalGridSx}
					fontFamily={'Poppins'}
					getRowId={(row) => row.patient_id}
				/>
			</Box>
		</div>
	);
}
